/** @format */

import type { Topic, TopicId } from 'src/types';

export const FAVORITE_VIEW_PARAM = 'favourite';

export const hasChildren = (item: Topic | null | undefined): boolean =>
  Array.isArray(item?.children) && item.children.length > 0;

export const getTopicPathTitles = (roots: Topic[] | null | undefined, path: number[]): string[] => {
  if (!Array.isArray(roots) || !Array.isArray(path) || path.length === 0) return [];
  const titles: string[] = [];
  let current: Topic[] | undefined = roots;
  for (const idx of path) {
    if (!Array.isArray(current) || current[idx] == null) break;
    const node = current[idx];
    titles.push(node?.displayName || node?.name || '');
    current = node?.children;
  }
  return titles.filter(Boolean);
};

export const collectFavoriteTopicsFlat = (nodes: Topic[] | null | undefined): Topic[] => {
  const result: Topic[] = [];
  const walk = (items: Topic[] | null | undefined) => {
    if (!Array.isArray(items)) return;
    items.forEach(node => {
      if (node?.favorite === true) {
        result.push(node);
      }
      if (hasChildren(node)) {
        walk(node.children);
      }
    });
  };
  walk(nodes);
  return result;
};

export const tryGetTopicId = (topic: Topic | null | undefined): TopicId | null => {
  const id = topic?.id;
  return typeof id === 'number' || typeof id === 'string' ? id : null;
};

export const getTopicTreeNodeKey = (topic: Topic | null | undefined, path?: number[]): string => {
  const id = tryGetTopicId(topic);
  if (id !== null) {
    return String(id);
  }
  return Array.isArray(path) ? path.join('-') : '';
};

export const isActiveExtranetTopic = (
  topic: Topic | null | undefined,
  activeTopicId: TopicId | null | undefined
): boolean => {
  const topicId = tryGetTopicId(topic);
  if (topicId === null || activeTopicId == null || activeTopicId === '') {
    return false;
  }
  return String(topicId) === String(activeTopicId);
};

export const getExpandedTopicKeysForActivePath = (pathNodes: Topic[] | null | undefined): Record<string, boolean> => {
  const keys: Record<string, boolean> = {};
  if (!Array.isArray(pathNodes) || pathNodes.length === 0) {
    return keys;
  }

  pathNodes.slice(0, -1).forEach(node => {
    const id = tryGetTopicId(node);
    if (id !== null) {
      keys[String(id)] = true;
    }
  });

  const leaf = pathNodes[pathNodes.length - 1];
  if (pathNodes.length < 3 && hasChildren(leaf)) {
    const leafId = tryGetTopicId(leaf);
    if (leafId !== null) {
      keys[String(leafId)] = true;
    }
  }

  return keys;
};

export const getTopicDisplayTitle = (topic: Topic | null | undefined): string =>
  String(topic?.displayName || topic?.name || '').trim();

export const findTopicByIdInTree = (
  nodes: Topic[] | null | undefined,
  targetId: TopicId | null | undefined
): Topic | null => {
  if (!Array.isArray(nodes) || targetId == null || targetId === '') {
    return null;
  }
  const normalized = String(targetId);
  for (const node of nodes) {
    if (String(node?.id) === normalized) {
      return node;
    }
    const found = findTopicByIdInTree(node?.children, targetId);
    if (found) {
      return found;
    }
  }
  return null;
};

export const findTopicPathInTree = (
  nodes: Topic[] | null | undefined,
  targetId: TopicId | null | undefined
): Topic[] => {
  if (!Array.isArray(nodes) || targetId == null || targetId === '') {
    return [];
  }
  const normalized = String(targetId);
  for (const node of nodes) {
    if (String(node?.id) === normalized) {
      return [node];
    }
    const childPath = findTopicPathInTree(node?.children, targetId);
    if (childPath.length > 0) {
      return [node, ...childPath];
    }
  }
  return [];
};

export const resolveExtranetContentHead = ({
  topicId,
  topicTree,
  topicDetail,
  fallbackTitle,
  fallbackPathTitles
}: {
  topicId?: TopicId | null;
  topicTree?: Topic[] | null;
  topicDetail?: Topic | null;
  fallbackTitle?: string | null;
  fallbackPathTitles?: string[] | null;
}): { title: string; pathTitles: string[] } => {
  const path = findTopicPathInTree(topicTree, topicId);
  const treeTitle = getTopicDisplayTitle(path[path.length - 1]);
  const detailTitle =
    topicDetail != null && topicId != null && topicId !== '' && String(topicDetail.id) === String(topicId)
      ? getTopicDisplayTitle(topicDetail)
      : '';
  const pathTitles =
    path.length > 1
      ? path.slice(0, -1).map(getTopicDisplayTitle).filter(Boolean)
      : Array.isArray(fallbackPathTitles)
        ? fallbackPathTitles.filter(Boolean)
        : [];

  return {
    title: detailTitle || treeTitle || String(fallbackTitle || '').trim(),
    pathTitles
  };
};

export const isExtranetVisibleTopic = (topic: Topic | null | undefined): boolean => topic?.published !== false;

export const resolveChildTopicsForReview = (
  topicId: TopicId | null | undefined,
  topicTree: Topic[] | null | undefined,
  topicDetail: Topic | null | undefined
): Topic[] => {
  const treeNode = topicId != null && topicId !== '' ? findTopicByIdInTree(topicTree, topicId) : null;
  const treeChildren = (treeNode && Array.isArray(treeNode.children) ? treeNode.children : []).filter(
    isExtranetVisibleTopic
  );
  const detailChildren =
    topicDetail != null &&
    topicId != null &&
    topicId !== '' &&
    String(topicDetail.id) === String(topicId) &&
    Array.isArray(topicDetail.children)
      ? topicDetail.children.filter(isExtranetVisibleTopic)
      : [];

  if (treeChildren.length === 0) {
    return detailChildren;
  }

  const detailById = new Map(detailChildren.map(child => [String(child.id), child]));
  return treeChildren.map(child => {
    const fromDetail = detailById.get(String(child.id));
    if (!fromDetail) {
      return child;
    }
    return {
      ...fromDetail,
      name: child.name || fromDetail.name,
      displayName: child.displayName || fromDetail.displayName
    };
  });
};

export const isFavoriteViewSearch = (searchString: string): boolean => {
  const params = new URLSearchParams(searchString.startsWith('?') ? searchString.slice(1) : searchString);
  return params.get('view') === FAVORITE_VIEW_PARAM;
};

export const buildExtranetLocation = ({
  topicId,
  search,
  favoriteView
}: {
  topicId?: string | number;
  search?: string;
  favoriteView?: boolean;
} = {}) => {
  const params = new URLSearchParams(search?.startsWith('?') ? search.slice(1) : search || '');
  if (favoriteView === true) {
    params.set('view', FAVORITE_VIEW_PARAM);
  } else if (favoriteView === false) {
    params.delete('view');
  }
  const normalizedTopicId = topicId != null ? String(topicId).trim() : '';
  const pathname = normalizedTopicId ? `/extranet/${normalizedTopicId}` : '/extranet';
  const nextSearch = params.toString();
  return { pathname, search: nextSearch ? `?${nextSearch}` : '' };
};
